import { Injectable } from '@angular/core';
import { DatasourceGitService } from '@y42/datasource';
import { ErrorHandlerService } from '@y42/error-handling';
import { Y42GitFolders } from '@y42/feature/git';
import { UI_MODELING_MODEL_DATA_MODEL_JSON_SCHEMA_URL } from '@y42/feature/json-schema';
import { GitQuery, GitService } from '@y42/git';
import { BaseSidebarObject, IS_SIDEBAR_TYPE_PROP } from '@y42/gojs';
import type { DataModelGitGoJSNode, UiModelingModelSettings } from '@y42/json-schema';
import { UiModelingSettingsGitModel } from './ui-modeling.model';

@Injectable({ providedIn: 'root' })
export class UiModelingGitService extends DatasourceGitService {
  constructor(
    protected readonly git: GitService,
    protected readonly gitQuery: GitQuery,
    protected readonly errorHandlerService: ErrorHandlerService,
  ) {
    super({
      name: 'Model',
      type: Y42GitFolders.UiModel,
      git,
      gitQuery,
      errorHandlerService,
      settingsGitModel: UiModelingSettingsGitModel,
      hydrationDefinition: {
        1: {
          modelType: 'folder',
          childType: 'folder',
        },
        2: {
          modelType: 'folder',
          models: {
            tests: {
              fileName: 'tests.json',
            },
          },
        },
      },
    });
  }

  async writeUiModelFile(path: string, model: UiModelingModelSettings) {
    const sanitizedModel = this.sanitizeUiModelingSettings(model);
    const sanitizedModelWithSchema = { ...sanitizedModel, $schema: UI_MODELING_MODEL_DATA_MODEL_JSON_SCHEMA_URL };
    await this.git.writeFile({ path, content: sanitizedModelWithSchema, skipSync: false });
  }

  private sanitizeUiModelingSettings(model: UiModelingModelSettings): UiModelingModelSettings {
    return {
      nodeDataArray: model.nodeDataArray.map((node) => {
        if ((node as BaseSidebarObject)[IS_SIDEBAR_TYPE_PROP]) {
          return node;
        } else {
          const goJsNode = node as DataModelGitGoJSNode;
          return {
            key: goJsNode.key,
            type: goJsNode.type,
            category: goJsNode.category,
            transformationStep: goJsNode.transformationStep,
            isGroup: goJsNode.isGroup,
            group: goJsNode.group,
            location: goJsNode.location,
          };
        }
      }),
      linkDataArray: model.linkDataArray,
    };
  }
}
